/**
 * PaymentNGN cbar-datatable runtime — window.CBAR helpers and cbar-datatable patches.
 * Loaded early via webcomponents.deps.bundle (imported from deps-entry.js) so the
 * window.CBAR helpers exist even if the main webcomponents bundle aborts mid-load
 * (e.g. a duplicate custom-element registration throws before it finishes).
 */
import { html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { createColumnHelper } from '@tanstack/lit-table';

/** Patches cbar-datatable before customElements.define upgrades in-DOM instances. */
let patchCbarDatatableRef = null;
const nativeCustomElementsDefine = customElements.define.bind(customElements);
customElements.define = function patchedCustomElementsDefine(name, constructor, options) {
  if (name === 'cbar-datatable' && typeof patchCbarDatatableRef === 'function') {
    patchCbarDatatableRef(constructor);
  }
  return nativeCustomElementsDefine(name, constructor, options);
};

const DATATABLE_TOOLTIP_OVERFLOW_STYLES = `
  :host td:has([data-tooltip]),
  :host td:has(.cbar-datatable-pill),
  :host td:has(.datatable-actions) {
    overflow: visible;
  }

  .table-container:has([data-tooltip]),
  .table-container:has(.cbar-datatable-pill),
  .table-container:has(.datatable-actions) {
    overflow: visible;
  }
`;

const DATATABLE_OUTER_SCROLL_STYLES = `
  .table-container {
    border: none !important;
    border-radius: 0 !important;
  }

  /* Let the outer .table-responsive own horizontal scrolling when wrapping
     is not enough; default layout fits the viewport width. */
  .table-wrapper,
  .table-container {
    overflow: visible !important;
    overflow-x: visible !important;
  }

  table {
    min-width: 700px;
  }

  th {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    vertical-align: bottom;
    hyphens: auto;
  }

  td {
    white-space: normal;
    vertical-align: top;
    hyphens: auto;
  }

  /* Keep action columns compact on a single icon row. */
  .action-column,
  th.action-column,
  td.action-column,
  td:has(.datatable-actions),
  th:has(.datatable-actions) {
    width: 1%;
    white-space: nowrap;
    word-break: normal;
    overflow-wrap: normal;
    vertical-align: middle;
  }

  ${DATATABLE_TOOLTIP_OVERFLOW_STYLES}

  /* Override @cbar/shared-styles global \`a { secondary }\` for cell text links. */
  :host td a:not(.datatable-action-link):not(.datatable-icon-button-wrap):not(.icon_link):not(.icon_button) {
    color: var(--cbar-sys-color-primary, #007AFF);
    text-decoration: none;
  }

  :host td a:not(.datatable-action-link):not(.datatable-icon-button-wrap):not(.icon_link):not(.icon_button):hover,
  :host td a:not(.datatable-action-link):not(.datatable-icon-button-wrap):not(.icon_link):not(.icon_button):focus {
    color: var(--cbar-sys-color-primary, #007AFF);
    text-decoration: underline;
  }

  :host td a:not(.datatable-action-link):not(.datatable-icon-button-wrap):not(.icon_link):not(.icon_button):visited {
    color: var(--cbar-sys-color-primary, #007AFF);
  }

  :host td :is(.label, .badge) a {
    color: inherit;
    text-decoration: none;
  }
`;

const SERVER_SIDE_TABLE_SHELL_STYLES = `
  ${DATATABLE_OUTER_SCROLL_STYLES}
`;

function injectDatatableOuterScrollStyles(table) {
  if (table.__cbarOuterScrollStylesInjected || !table.shadowRoot) {
    return;
  }
  table.__cbarOuterScrollStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = DATATABLE_OUTER_SCROLL_STYLES;
  table.shadowRoot.appendChild(style);
}

function injectServerSideTableShellStyles(table) {
  injectDatatableOuterScrollStyles(table);
}

window.CBAR = window.CBAR || {};
window.CBAR.applyDatatableOuterScrollShell = function applyDatatableOuterScrollShell(table) {
  if (!table) {
    return;
  }
  injectDatatableOuterScrollStyles(table);
};

window.CBAR.applyServerSideDatatableShell = function applyServerSideDatatableShell(table) {
  if (!table || table.dataset.serverSide !== 'true') {
    return;
  }
  table.deferColumnInference = true;
  table.enablePagination = false;
  injectServerSideTableShellStyles(table);
};

window.CBAR.html = html;
window.CBAR.unsafeHTML = unsafeHTML;
window.CBAR.createColumnHelper = createColumnHelper;

const DATATABLE_ACTION_ICON_VARS = '--cbar-icon-size:18px;--md-icon-size:18px;';

/** Render server-provided HTML strings in cbar-datatable cells (TanStack + lit-table). */
window.CBAR.datatableHtmlCell = function (value) {
  if (value == null || value === '') {
    return '';
  }
  return html`${unsafeHTML(value)}`;
};

/** Wrap action-column HTML so legacy Bootstrap btn links render as pill buttons. */
window.CBAR.datatableActionsCell = function (value, options) {
  if (value == null || value === '') {
    return '';
  }

  const raw = String(value);
  if (/class="[^"]*datatable-actions/i.test(raw)) {
    return html`${unsafeHTML(raw)}`;
  }

  const layoutStyle = `${DATATABLE_ACTION_ICON_VARS}display:inline-flex;align-items:center;gap:3px;flex-wrap:nowrap;`;
  return html`<div class="datatable-actions datatable-actions--inline" style="${layoutStyle}">${unsafeHTML(raw)}</div>`;
};

window.CBAR.datatableInlineActionsCell = function (value) {
  return window.CBAR.datatableActionsCell(value, { inline: true });
};

window.CBAR.payorDatatableHtmlCell = window.CBAR.datatableActionsCell;

function resolveDatatableColumnHeaderLabel(header) {
  if (typeof header === 'string') {
    const trimmed = header.trim();
    return trimmed || null;
  }
  if (typeof header === 'function') {
    try {
      const result = header();
      if (typeof result === 'string') {
        const trimmed = result.trim();
        return trimmed || null;
      }
    } catch (_) {
      // Some header renderers require TanStack context.
    }
  }
  return null;
}

/** Resolve function headers to plain strings so column visibility shows human labels. */
function normalizeDatatableColumns(columns) {
  if (!Array.isArray(columns)) {
    return columns;
  }
  let changed = false;
  const normalized = columns.map((column) => {
    if (!column || typeof column !== 'object' || typeof column.header === 'string') {
      return column;
    }
    const label = resolveDatatableColumnHeaderLabel(column.header);
    if (label) {
      changed = true;
      return { ...column, header: label };
    }
    return column;
  });
  return changed ? normalized : columns;
}

window.CBAR.normalizeDatatableColumns = normalizeDatatableColumns;

const DATATABLE_ACTION_STYLES = css`
  .table-wrapper {
    overflow-x: auto;
  }

  /* Keep filter fields from butting against the next column/cell edge. */
  th .filter-input,
  th .filter-select {
    box-sizing: border-box;
    width: calc(100% - var(--cbar-sys-spacing-2, 8px));
    max-width: 100%;
    margin-right: var(--cbar-sys-spacing-2, 8px);
  }

  td:has(.datatable-actions),
  th:has(.datatable-actions) {
    overflow: visible;
  }

  .table-container:has(.datatable-actions) {
    overflow: visible;
  }

  .datatable-actions {
    --cbar-datatable-action-gap: 3px;
    display: inline-flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: var(--cbar-datatable-action-gap);
    width: max-content;
    max-width: none;
    --cbar-icon-size: 18px;
    --md-icon-size: 18px;
  }

  .datatable-actions--inline {
    display: inline-flex !important;
    flex-flow: row nowrap !important;
    align-items: center;
    gap: var(--cbar-datatable-action-gap, 3px) !important;
    width: max-content;
    max-width: none;
  }

  .datatable-actions--inline > a,
  .datatable-actions--inline > .datatable-action-link,
  .datatable-actions--inline > cbar-icon-button,
  .datatable-actions--inline > a.datatable-icon-button-wrap,
  .datatable-actions--inline > .datatable-icon-indicator,
  .datatable-actions--inline > span {
    margin: 0;
    flex: 0 0 auto;
  }

  /* PHP cells already wrap icons; avoid double-flex nesting from legacy wrappers. */
  .datatable-actions:not(.datatable-actions--inline) > .datatable-actions--inline {
    display: contents;
  }

  .datatable-actions cbar-icon-button {
    --cbar-icon-button-icon-size: 18px;
  }

  .datatable-actions a.datatable-icon-button-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    text-decoration: none;
    line-height: 0;
  }

  .datatable-actions a.datatable-icon-button-wrap cbar-icon-button {
    pointer-events: none;
  }

  .datatable-actions a.cleanlink,
  .datatable-actions a.datatable-action-link,
  .datatable-actions a.icon_link,
  .datatable-actions a.icon_button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 18px;
    min-width: 18px;
    padding: 0;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: var(--cbar-sys-color-on-surface-variant, #49454f);
    font-size: inherit;
    font-weight: normal;
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .datatable-actions cbar-icon {
    --cbar-icon-size: 18px !important;
    --md-icon-size: 18px !important;
    font-size: 18px !important;
    width: 18px !important;
    height: 18px !important;
    flex-shrink: 0;
  }

  .datatable-actions [data-tooltip] {
    position: relative;
  }

  .datatable-actions a.cleanlink:hover,
  .datatable-actions a.datatable-action-link:hover,
  .datatable-actions a.icon_link:hover,
  .datatable-actions a.icon_button:hover {
    filter: none;
    background: var(--cbar-sys-color-surface-container-highest, #e6e0e9);
    color: var(--cbar-sys-color-primary, #6750a4);
  }

  .datatable-actions a.rec_btn_button,
  .datatable-actions .rec_btn_button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    padding: 4px 12px;
    border-radius: 16px;
    border: 1px solid transparent;
    font-size: var(--cbar-sys-typescale-label-medium-size, 0.75rem);
    font-weight: 500;
    line-height: 1.2;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: filter 0.15s ease;
  }

  .datatable-actions a.btn-success,
  .datatable-actions .rec_btn_button.btn-success,
  .datatable-actions a.btn-primary,
  .datatable-actions .rec_btn_button.btn-primary {
    background: var(--cbar-sys-color-primary, #003f83);
    color: var(--cbar-sys-color-on-primary, #fff);
  }

  .datatable-actions a.btn-info,
  .datatable-actions .rec_btn_button.btn-info {
    background: var(--cbar-sys-color-secondary-container, #d7e3ff);
    color: var(--cbar-sys-color-on-secondary-container, #001d35);
    border-color: var(--cbar-sys-color-outline-variant, #c4c7c5);
  }

  .datatable-actions a.rec_btn_button:hover,
  .datatable-actions .rec_btn_button:hover {
    filter: brightness(1.06);
  }

  .datatable-reconciliation-actions,
  .datatable-actions .datatable-reconciliation-actions,
  .datatable-actions cbar-chip-set.datatable-reconciliation-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
    width: 100%;
    overflow: visible;
  }

  .datatable-reconciliation-actions cbar-chip,
  .datatable-actions .datatable-reconciliation-actions cbar-chip,
  .datatable-actions cbar-chip-set.datatable-reconciliation-actions cbar-chip {
    display: inline-flex;
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
    cursor: pointer;
  }

  .datatable-reconciliation-actions cbar-chip.cbar-chip--filled-primary[elevated],
  .datatable-actions .datatable-reconciliation-actions cbar-chip.cbar-chip--filled-primary[elevated] {
    --cbar-chip-elevated-container-color: var(--cbar-sys-color-primary, #003f83);
    --cbar-chip-label-text-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-hover-label-text-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-focus-label-text-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-pressed-label-text-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-outline-color: var(--cbar-sys-color-primary, #003f83);
    --cbar-chip-hover-state-layer-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-focus-state-layer-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-pressed-state-layer-color: var(--cbar-sys-color-on-primary, #fff);
  }

  .datatable-reconciliation-actions cbar-chip.cbar-chip--filled-info[elevated],
  .datatable-actions .datatable-reconciliation-actions cbar-chip.cbar-chip--filled-info[elevated] {
    --cbar-chip-elevated-container-color: var(--cbar-sys-color-secondary-container, #d7e3ff);
    --cbar-chip-label-text-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-hover-label-text-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-focus-label-text-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-pressed-label-text-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-outline-color: var(--cbar-sys-color-secondary-container, #d7e3ff);
    --cbar-chip-hover-state-layer-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-focus-state-layer-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-pressed-state-layer-color: var(--cbar-sys-color-on-secondary-container, #001d35);
  }

  td cbar-chip,
  td cbar-chip-set cbar-chip {
    display: inline-flex;
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
    cursor: pointer;
  }

  td cbar-chip.cbar-chip--filled-primary[elevated],
  td cbar-chip-set cbar-chip.cbar-chip--filled-primary[elevated] {
    --cbar-chip-elevated-container-color: var(--cbar-sys-color-primary, #003f83);
    --cbar-chip-label-text-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-hover-label-text-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-focus-label-text-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-pressed-label-text-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-outline-color: var(--cbar-sys-color-primary, #003f83);
    --cbar-chip-hover-state-layer-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-focus-state-layer-color: var(--cbar-sys-color-on-primary, #fff);
    --cbar-chip-pressed-state-layer-color: var(--cbar-sys-color-on-primary, #fff);
  }

  td cbar-chip.cbar-chip--filled-info[elevated],
  td cbar-chip-set cbar-chip.cbar-chip--filled-info[elevated] {
    --cbar-chip-elevated-container-color: var(--cbar-sys-color-secondary-container, #d7e3ff);
    --cbar-chip-label-text-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-hover-label-text-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-focus-label-text-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-pressed-label-text-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-outline-color: var(--cbar-sys-color-secondary-container, #d7e3ff);
    --cbar-chip-hover-state-layer-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-focus-state-layer-color: var(--cbar-sys-color-on-secondary-container, #001d35);
    --cbar-chip-pressed-state-layer-color: var(--cbar-sys-color-on-secondary-container, #001d35);
  }

  td cbar-chip.cbar-chip--filled-success[elevated],
  td cbar-chip-set cbar-chip.cbar-chip--filled-success[elevated] {
    --cbar-chip-elevated-container-color: var(--cbar-sys-color-success, #27c24c);
    --cbar-chip-label-text-color: var(--cbar-sys-color-on-success, #fff);
    --cbar-chip-hover-label-text-color: var(--cbar-sys-color-on-success, #fff);
    --cbar-chip-focus-label-text-color: var(--cbar-sys-color-on-success, #fff);
    --cbar-chip-pressed-label-text-color: var(--cbar-sys-color-on-success, #fff);
    --cbar-chip-outline-color: var(--cbar-sys-color-success, #27c24c);
    --cbar-chip-hover-state-layer-color: var(--cbar-sys-color-on-success, #fff);
    --cbar-chip-focus-state-layer-color: var(--cbar-sys-color-on-success, #fff);
    --cbar-chip-pressed-state-layer-color: var(--cbar-sys-color-on-success, #fff);
  }

  .datatable-chip-list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    max-width: 28rem;
  }

  .datatable-chip-list__visible,
  .datatable-chip-list__hidden {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }

  .datatable-chip-list__more {
    display: block;
  }

  .datatable-chip-list__more > summary {
    list-style: none;
    display: block;
    padding-block: 8px;
  }

  .datatable-chip-list__more > summary::-webkit-details-marker {
    display: none;
  }

  .datatable-chip-list__more[open] .datatable-chip-list__hidden {
    margin-top: 16px;
  }

  .datatable-chip-list__toggle {
    color: var(--cbar-sys-color-primary, #007aff);
    font-size: var(--cbar-sys-typescale-body-small-size, 0.8125rem);
    font-weight: var(--cbar-sys-typescale-label-medium-weight, 500);
    cursor: pointer;
  }

  .datatable-chip-list__toggle:hover,
  .datatable-chip-list__toggle:focus-visible {
    text-decoration: underline;
    outline: none;
  }

  .datatable-chip-list__toggle-label--less {
    display: none;
  }

  .datatable-chip-list__more[open] .datatable-chip-list__toggle-label--more {
    display: none;
  }

  .datatable-chip-list__more[open] .datatable-chip-list__toggle-label--less {
    display: inline;
  }

  .datatable-reconciliation-actions a.rec_btn_button,
  .datatable-actions .datatable-reconciliation-actions a.rec_btn_button {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }

  /* Cell text links — override shared-styles global anchor secondary color */
  :host td a:not(.datatable-action-link):not(.datatable-icon-button-wrap):not(.icon_link):not(.icon_button) {
    color: var(--cbar-sys-color-primary, #007AFF);
    text-decoration: none;
  }

  :host td a:not(.datatable-action-link):not(.datatable-icon-button-wrap):not(.icon_link):not(.icon_button):hover,
  :host td a:not(.datatable-action-link):not(.datatable-icon-button-wrap):not(.icon_link):not(.icon_button):focus {
    color: var(--cbar-sys-color-primary, #007AFF);
    text-decoration: underline;
  }

  :host td a:not(.datatable-action-link):not(.datatable-icon-button-wrap):not(.icon_link):not(.icon_button):visited {
    color: var(--cbar-sys-color-primary, #007AFF);
  }

  td :is(.label, .badge) a {
    color: inherit;
    text-decoration: none;
  }

  .sales-list-title-link {
    display: inline-flex;
    align-items: center;
    gap: var(--cbar-sys-spacing-2, 8px);
    text-decoration: none;
  }

  .sales-list-title-link:hover {
    text-decoration: underline;
  }

  .sales-list-title-link__image {
    flex-shrink: 0;
    width: 20px;
    max-width: 20px;
    max-height: 20px;
    height: auto;
    object-fit: contain;
    border-radius: var(--cbar-sys-shape-corner-extra-small-default-size, 3px);
  }

  .sales-list-title-link__text {
    line-height: var(--cbar-sys-typescale-body-medium-line-height, 1.25rem);
  }

  .cbar-datatable-amount {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

function parseDatatableJson(raw) {
  if (raw == null || raw === '') {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw !== 'string') {
    return [];
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

window.CBAR.parseDatatableJson = parseDatatableJson;

/** Ensure cbar-datatable.data is always an array (Lit sets null when attribute JSON fails). */
function ensureDatatableDataArray(table) {
  if (!table) {
    return [];
  }

  if (Array.isArray(table.data)) {
    return table.data;
  }

  table.data = [];
  return table.data;
}

function hydrateDatatableData(table) {
  if (!table) {
    return [];
  }

  if (
    table.__cbarProgrammaticData
    && Array.isArray(table.data)
    && table.data.length > 0
  ) {
    return table.data;
  }

  if (Array.isArray(table.data) && table.data.length > 0) {
    return table.data;
  }

  const script = table.id ? document.getElementById(`${table.id}-data`) : null;
  if (script?.textContent) {
    table.data = parseDatatableJson(script.textContent);
    return table.data;
  }

  const rawAttr = table.getAttribute('data');
  if (rawAttr != null) {
    const trimmed = rawAttr.trim();
    if (trimmed !== '' && trimmed !== '[]') {
      table.data = parseDatatableJson(rawAttr);
      return table.data;
    }
  }

  return ensureDatatableDataArray(table);
}

window.CBAR.hydrateDatatableData = hydrateDatatableData;

function setDatatablePageSize(table, pageSize) {
  const normalizedPageSize = Math.max(1, Number(pageSize) || 10);
  if (typeof table._handlePaginationChange === 'function') {
    table._handlePaginationChange({ pageIndex: 0, pageSize: normalizedPageSize });
    return;
  }

  table._pagination = { pageIndex: 0, pageSize: normalizedPageSize };
  if (typeof table.requestUpdate === 'function') {
    table.requestUpdate();
  }
}

window.CBAR.configureServerSideDatatable = function (table, pageSize) {
  if (!table) {
    return false;
  }

  table.enablePagination = false;
  setDatatablePageSize(table, pageSize);
  return true;
};

window.CBAR.setServerSideDatatableData = function (table, data, pageSize) {
  if (!table) {
    return false;
  }

  window.CBAR.configureServerSideDatatable(table, pageSize);
  table.data = Array.isArray(data) ? data : [];
  if (typeof table.requestUpdate === 'function') {
    table.requestUpdate();
  }
  return true;
};

/** Find [data-tooltip] action link in event path (works across shadow DOM). */
function findDatatableActionTrigger(event) {
  const path = event.composedPath();

  for (const node of path) {
    if (!(node instanceof Element)) {
      continue;
    }
    if (node.hasAttribute('data-tooltip') && (node.closest('.datatable-actions') || node.closest('cbar-datatable'))) {
      return node;
    }
  }

  return null;
}

const DATATABLE_ACTION_TOOLTIP_GAP = 8;
const DATATABLE_ACTION_TOOLTIP_VIEWPORT_PAD = 8;

const DATATABLE_ACTION_TOOLTIP_STYLE = [
  'position:fixed',
  'z-index:10000',
  'padding:4px 8px',
  'border-radius:4px',
  'background:#322f35',
  'color:#f5eff7',
  'font:500 12px/1.3 Roboto, sans-serif',
  'white-space:nowrap',
  'pointer-events:none',
  'box-shadow:0 1px 3px rgba(0,0,0,0.28)',
  '-webkit-font-smoothing:antialiased',
  '-moz-osx-font-smoothing:grayscale',
  'display:none',
].join(';');

/** Fixed-position tooltips — CSS ::after is clipped by table overflow containers. */
function setupDatatableActionTooltips() {
  if (window.CBAR.__datatableActionTooltipsReady) {
    return;
  }
  window.CBAR.__datatableActionTooltipsReady = true;

  const tooltip = document.createElement('div');
  tooltip.className = 'cbar-datatable-action-tooltip';
  tooltip.setAttribute('role', 'tooltip');
  tooltip.setAttribute('aria-hidden', 'true');
  tooltip.style.cssText = DATATABLE_ACTION_TOOLTIP_STYLE;

  const mount = () => {
    if (!tooltip.isConnected) {
      document.body.appendChild(tooltip);
    }
  };

  if (document.body) {
    mount();
  } else {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  }

  let activeTrigger = null;

  const hide = () => {
    activeTrigger = null;
    tooltip.style.display = 'none';
    tooltip.textContent = '';
    tooltip.removeAttribute('data-placement');
    tooltip.style.removeProperty('--cbar-tooltip-arrow-left');
    tooltip.setAttribute('aria-hidden', 'true');
  };

  const position = (trigger) => {
    const rect = trigger.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const tipWidth = tooltip.offsetWidth;
    const tipHeight = tooltip.offsetHeight;
    const pad = DATATABLE_ACTION_TOOLTIP_VIEWPORT_PAD;
    const maxLeft = Math.max(pad, window.innerWidth - tipWidth - pad);
    let left = Math.min(Math.max(centerX - tipWidth / 2, pad), maxLeft);
    let top = rect.top - tipHeight - DATATABLE_ACTION_TOOLTIP_GAP;
    let placement = 'above';

    if (top < pad) {
      top = rect.bottom + DATATABLE_ACTION_TOOLTIP_GAP;
      placement = 'below';
      const maxTop = Math.max(pad, window.innerHeight - tipHeight - pad);
      top = Math.min(Math.max(top, pad), maxTop);
    }

    const arrowLeft = Math.min(Math.max(centerX - left, 12), Math.max(12, tipWidth - 12));
    tooltip.dataset.placement = placement;
    tooltip.style.setProperty('--cbar-tooltip-arrow-left', `${Math.round(arrowLeft)}px`);
    tooltip.style.transform = 'none';
    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
  };

  const show = (trigger) => {
    mount();
    const text = trigger.getAttribute('data-tooltip');
    if (!text) {
      hide();
      return;
    }
    activeTrigger = trigger;
    tooltip.textContent = text;
    tooltip.style.display = 'block';
    tooltip.setAttribute('aria-hidden', 'false');
    position(trigger);
  };

  const updateFromEvent = (event) => {
    const trigger = findDatatableActionTrigger(event);
    if (trigger) {
      if (trigger !== activeTrigger) {
        show(trigger);
      } else if (tooltip.style.display === 'block') {
        position(trigger);
      }
      return;
    }
    hide();
  };

  document.addEventListener('pointermove', updateFromEvent, { passive: true });
  document.addEventListener('pointerdown', updateFromEvent, { passive: true });

  document.addEventListener(
    'scroll',
    () => {
      if (activeTrigger && tooltip.style.display === 'block') {
        position(activeTrigger);
      }
    },
    true,
  );

  window.addEventListener('resize', () => {
    if (activeTrigger && tooltip.style.display === 'block') {
      position(activeTrigger);
    }
  });

  document.documentElement.addEventListener('mouseleave', hide, { passive: true });
}

function ensureDatatableActionTooltips() {
  setupDatatableActionTooltips();
}

ensureDatatableActionTooltips();
document.addEventListener('DOMContentLoaded', ensureDatatableActionTooltips, { once: true });

const DEFAULT_AVAILABLE_SIZES = ['10', '25', '50'];

/**
 * Lit's Array property converter expects JSON (e.g. `["10","25","50"]`).
 * Comma-separated `available-sizes="10,25,50"` parses to null and crashes
 * cbar-datatable connectedCallback when it reads availableSizes[0].
 * Normalize to a string array before the original connectedCallback runs.
 */
function normalizeAvailableSizes(table) {
  if (Array.isArray(table.availableSizes) && table.availableSizes.length > 0) {
    return;
  }

  const raw = table.getAttribute('available-sizes');
  if (!raw) {
    table.availableSizes = [...DEFAULT_AVAILABLE_SIZES];
    return;
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      table.availableSizes = parsed.map(String);
      return;
    }
  } catch (error) {
    // fall through to comma-separated parsing
  }

  const sizes = raw
    .split(',')
    .map((size) => size.trim())
    .filter(Boolean);
  table.availableSizes = sizes.length > 0 ? sizes : [...DEFAULT_AVAILABLE_SIZES];
}

function patchCbarDatatable(CbarDatatable) {
  if (!CbarDatatable || CbarDatatable.__cbarDatatablePatched) {
    return;
  }
  CbarDatatable.__cbarDatatablePatched = true;

  if (!CbarDatatable.__cbarActionStylesPatched) {
    CbarDatatable.__cbarActionStylesPatched = true;
    const existingStyles = CbarDatatable.styles;
    CbarDatatable.styles = Array.isArray(existingStyles)
      ? [...existingStyles, DATATABLE_ACTION_STYLES]
      : [existingStyles, DATATABLE_ACTION_STYLES];
  }

  const originalConnectedCallback = CbarDatatable.prototype.connectedCallback;
  CbarDatatable.prototype.connectedCallback = function () {
    normalizeAvailableSizes(this);

    if (typeof originalConnectedCallback === 'function') {
      originalConnectedCallback.call(this);
    }

    window.CBAR?.applyDatatableOuterScrollShell?.(this);

    if (this.dataset.serverSide === 'true') {
      this.enablePagination = false;
      window.CBAR?.applyServerSideDatatableShell?.(this);
    }

    hydrateDatatableData(this);
  };

  const originalWillUpdate = CbarDatatable.prototype.willUpdate;
  CbarDatatable.prototype.willUpdate = function (changedProperties) {
    ensureDatatableDataArray(this);

    if (!this.__cbarDataHydrated || changedProperties.has('data')) {
      hydrateDatatableData(this);
      this.__cbarDataHydrated = true;
    }

    if (changedProperties.has('columns') && Array.isArray(this.columns)) {
      const needsNormalize = this.columns.some((column) => typeof column?.header === 'function');
      if (needsNormalize) {
        this.columns = normalizeDatatableColumns(this.columns);
      }
    }

    if (typeof originalWillUpdate === 'function') {
      originalWillUpdate.call(this, changedProperties);
    }
  };

  const originalUpdated = CbarDatatable.prototype.updated;
  CbarDatatable.prototype.updated = function (changedProperties) {
    if (typeof originalUpdated === 'function') {
      originalUpdated.call(this, changedProperties);
    }
    window.CBAR?.applyDatatableOuterScrollShell?.(this);
    if (this.dataset.serverSide === 'true') {
      this.enablePagination = false;
      window.CBAR?.applyServerSideDatatableShell?.(this);
    }
  };
}

const CbarDatatable = customElements.get('cbar-datatable');
patchCbarDatatableRef = patchCbarDatatable;
if (CbarDatatable) {
  patchCbarDatatable(CbarDatatable);
  document.querySelectorAll('cbar-datatable').forEach((table) => {
    normalizeAvailableSizes(table);
    window.CBAR?.applyDatatableOuterScrollShell?.(table);
  });
} else {
  customElements.whenDefined('cbar-datatable').then(() => {
    const Datatable = customElements.get('cbar-datatable');
    patchCbarDatatable(Datatable);
    document.querySelectorAll('cbar-datatable').forEach((table) => {
      normalizeAvailableSizes(table);
    });
  });
}

/** Apply column defs and force a full row re-render (fixes stale text cells after async init). */
window.CBAR.assignHtmlDatatableColumns = function (table, columns) {
  if (!table || !Array.isArray(columns)) {
    return false;
  }
  hydrateDatatableData(table);
  const normalized = normalizeDatatableColumns(columns);
  table.columns = normalized;
  if (Array.isArray(table.data)) {
    table.data = table.data.slice();
  }
  if (typeof table.requestUpdate === 'function') {
    table.requestUpdate();
  }
  return true;
};

/** Update datatable row data without rebuilding column definitions. */
window.CBAR.setDatatableData = function (table, data) {
  if (!table) {
    return false;
  }
  const rows = Array.isArray(data) ? data : [];
  table.__cbarProgrammaticData = rows.length > 0;
  table.data = rows;
  table.removeAttribute('data');
  if (typeof table.requestUpdate === 'function') {
    table.requestUpdate();
  }
  return true;
};
